import { z } from 'zod';
import { SourceRef } from './source-ref.js';

/**
 * Epistemic status of a material output field (ADR-018, docs/04-KNOWLEDGE-MEMORY.md).
 * A model claiming VERIFIED does not make it VERIFIED: a deterministic verifier downgrades
 * VERIFIED to INFERRED unless the evidence resolves to material retrieved during the execution
 * or to VALIDATED organizational knowledge. The verifier ships with the execution engine
 * (Increment 2). This schema enforces the structural invariants only.
 */
export const ASSESSMENT_STATUSES = [
  'VERIFIED',
  'INFERRED',
  'UNKNOWN',
  'NOT_FOUND',
  'REQUIRES_CONFIRMATION',
  'CONFLICT',
] as const;
export const AssessmentStatus = z.enum(ASSESSMENT_STATUSES);
export type AssessmentStatus = z.infer<typeof AssessmentStatus>;

export type Assessed<T> = {
  value: T | null;
  status: AssessmentStatus;
  evidence: SourceRef[];
  confidence: number;
  rationale?: string;
  conflictId?: string;
};

/** Builds a Zod schema for `Assessed<T>` around a value schema. */
export function assessed<T extends z.ZodType>(value: T) {
  return z
    .object({
      value: value.nullable(),
      status: AssessmentStatus,
      evidence: z.array(SourceRef),
      confidence: z.number().min(0).max(1),
      rationale: z.string().optional(),
      conflictId: z.string().optional(),
    })
    .superRefine((parsed, ctx) => {
      // Generic T defeats inference of the object output type; the shape is known here.
      const v = parsed as Assessed<unknown>;
      if (v.status === 'VERIFIED' && v.evidence.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'VERIFIED requires at least one evidence SourceRef',
        });
      }
      if ((v.status === 'UNKNOWN' || v.status === 'NOT_FOUND') && v.value !== null) {
        ctx.addIssue({ code: 'custom', message: `${v.status} must not carry a value` });
      }
      if (v.status === 'CONFLICT' && !v.conflictId) {
        ctx.addIssue({ code: 'custom', message: 'CONFLICT requires conflictId' });
      }
    });
}

/**
 * LLM-facing claim format. Models cannot produce SourceRefs or hashes; they cite the context
 * source id they were given and a verbatim quote. The deterministic verifier converts claims
 * into `Assessed<T>` (resolving SourceRefs, hashing quotes) and downgrades unsupported VERIFIED.
 */
export function claim<T extends z.ZodType>(value: T) {
  return z.object({
    value: value.nullable(),
    status: AssessmentStatus,
    evidence: z
      .array(
        z.object({
          sourceId: z.string().max(20).describe('Id of a context source, e.g. "S1"'),
          quote: z.string().max(500).describe('Verbatim text copied from that source'),
        }),
      )
      .max(10),
    confidence: z.number().min(0).max(1),
    rationale: z.string().max(1000).optional(),
  });
}
